import { Role } from '@caresend/types';
import { asyncTimeout, formatCamelCase, hasStatus, isEqual, diffObjectsDeep } from '@caresend/utils';
import { defineComponent, computed, watch } from 'vue';
import __vue_component__ from '../../../../buttons/ButtonComponent/ButtonComponent.vue.js';
import { ButtonStyle } from '../../../../buttons/ButtonComponent/model.js';
import __vue_component__$4 from '../ItineraryList/components/ExpandCopyID.vue.js';
import { usePortalStore } from '../../store/portal/index.js';
import { WorkflowInlineComponentRecord } from '../../store/portal/workflow/componentMap.js';
import { getUniqueWorkflowItemID } from '../../store/portal/workflow/helpers.js';
import { useItineraryWorkflowsStore } from '../../store/portal/workflow/itinerary.js';
import { WorkflowItemStatus } from '../../store/portal/workflow/model.js';
import { workflowUtils } from '../../store/portal/workflow/utils.js';
import TransitionExpand from '../../../../transitions/TransitionExpand/TransitionExpand.js';
import __vue_component__$3 from '../../../../ui/Icon.vue.js';
import __vue_component__$1 from '../../../../ui/InteractivePopup/InteractivePopup.vue.js';
import { TagStyle } from '../../../../ui/Tag/model.js';
import __vue_component__$5 from '../../../../ui/Tag/Tag.vue.js';
import __vue_component__$2 from '../../../../ui/Tooltip/Tooltip.vue.js';

var script = defineComponent({
    name: 'InlineWorkflowItem',
    components: {
        ButtonComponent: __vue_component__,
        InteractivePopup: __vue_component__$1,
        TransitionExpand,
        Tooltip: __vue_component__$2,
        Icon: __vue_component__$3,
        ExpandCopyID: __vue_component__$4,
        Tag: __vue_component__$5,
    },
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        id: {
            type: String,
            required: true,
        },
        context: {
            type: [String, Number],
            required: true,
        },
        workflow: {
            type: Array,
            default: () => [],
        },
        parentID: {
            type: String,
            default: null,
        },
        storeRef: {
            type: String,
            required: true,
        },
    },
    setup(props) {
        const itineraryWorkflowsStore = useItineraryWorkflowsStore();
        const portalStore = usePortalStore();
        const { viewAs } = portalStore;
        const { getNextActionable, getIsWorkflowItemUnlocked, getIsChildWorkflowComplete, getActionableNonBlockingItems, getIsChildWorkflowFailed, } = workflowUtils;
        const nextActionableItem = computed(() => getNextActionable(props.workflow));
        const isUnlocked = computed(() => {
            if (!nextActionableItem.value)
                return false;
            return getIsWorkflowItemUnlocked(nextActionableItem.value, portalStore);
        });
        const isChildWorkflowComplete = computed(() => {
            if (!nextActionableItem.value)
                return false;
            return getIsChildWorkflowComplete(nextActionableItem.value, portalStore);
        });
        const actionableNonBlockingItems = computed(() => getActionableNonBlockingItems(props.workflow));
        const allowAction = (item) => {
            if (item.recordMeta.childWorkflow) {
                if (!getIsChildWorkflowComplete(item, portalStore)) {
                    return false;
                }
            }
            if (isUnlocked.value) {
                return isEqual(item, nextActionableItem.value);
            }
            return false;
        };
        const allowNextActionableItem = computed(() => !!nextActionableItem.value && allowAction(nextActionableItem.value));
        /**
         * Logic to automatically mount the workflow item's modal status to the store.
         *
         * Watching this within this component context (rather in than the store)
         * since each workflow's actionable item is unique to each workflow.
         * This value will likely never change at the same time across multiple workflows
         */
        watch(nextActionableItem, async (newVal, oldVal) => {
            var _a;
            if (viewAs[Role.SUPERADMIN])
                return;
            if (!oldVal)
                return;
            if (!newVal)
                return;
            if ((newVal === null || newVal === void 0 ? void 0 : newVal.id) === (oldVal === null || oldVal === void 0 ? void 0 : oldVal.id))
                return;
            if ((_a = oldVal === null || oldVal === void 0 ? void 0 : oldVal.options.modal) === null || _a === void 0 ? void 0 : _a.completeOnClose)
                return;
            if (!allowNextActionableItem.value)
                return;
            if (newVal.options.component)
                return;
            if (!newVal.options.modal) {
                itineraryWorkflowsStore.modalState.set.workflowItem(null);
                return;
            }
            if (newVal.options.modal.manualTrigger)
                return;
            await asyncTimeout(500);
            itineraryWorkflowsStore.modalState.set.workflowItem(newVal);
        }, { immediate: true });
        const inlineKey = computed(() => {
            if (!nextActionableItem.value)
                return;
            return `${props.id}-${getUniqueWorkflowItemID(nextActionableItem.value)}`;
        });
        const componentDef = computed(() => {
            var _a, _b;
            if (!allowNextActionableItem.value)
                return;
            if (!((_a = nextActionableItem.value) === null || _a === void 0 ? void 0 : _a.options.component))
                return;
            const { name, props: componentProps } = nextActionableItem.value.options.component;
            const component = WorkflowInlineComponentRecord[name];
            if (!component)
                return;
            return {
                component,
                props: componentProps,
                key: (_b = inlineKey.value) !== null && _b !== void 0 ? _b : '',
            };
        });
        const shouldDisplayButton = computed(() => {
            if (!nextActionableItem.value || !isUnlocked.value)
                return false;
            return !!nextActionableItem.value.options.modal;
        });
        const buttonProps = computed(() => {
            var _a;
            if (!nextActionableItem.value)
                return;
            const p = (_a = nextActionableItem.value.options.modal) === null || _a === void 0 ? void 0 : _a.buttonProps;
            return {
                ...p,
                buttonStyle: (p === null || p === void 0 ? void 0 : p.buttonStyle) || ButtonStyle.PRIMARY,
                iconRight: (p === null || p === void 0 ? void 0 : p.iconRight) || 'chevron-right',
                label: (p === null || p === void 0 ? void 0 : p.label) || 'Continue',
                subtitle: (p === null || p === void 0 ? void 0 : p.subtitle) || '',
            };
        });
        const openModal = () => {
            if (!nextActionableItem.value)
                return;
            itineraryWorkflowsStore.modalState.set.workflowItem(nextActionableItem.value);
        };
        const handleComplete = (workflowItem) => {
            const item = workflowItem !== null && workflowItem !== void 0 ? workflowItem : nextActionableItem.value;
            if (!item)
                return;
            itineraryWorkflowsStore.completeWorkflowItem(item);
        };
        const logItem = (workflowItem) => {
            console.info(JSON.stringify(workflowItem, null, 2));
            getIsWorkflowItemUnlocked(workflowItem, portalStore, true);
            const isComplete = getIsChildWorkflowComplete(workflowItem, portalStore, undefined, false, true);
            const isFailureWorkflowComplete = getIsChildWorkflowComplete(workflowItem, portalStore, undefined, true, true);
            const isPartialFailed = getIsChildWorkflowFailed(workflowItem, portalStore, undefined, 'partial');
            const isFullyFailed = getIsChildWorkflowFailed(workflowItem, portalStore, undefined, 'full');
            console.info('isComplete', isComplete);
            console.info('isFailureWorkflowComplete', isFailureWorkflowComplete);
            console.info('isPartialFailed', isPartialFailed);
            console.info('isFullyFailed', isFullyFailed);
            console.info('next actionable', nextActionableItem.value);
            console.info('actionable non blocking', actionableNonBlockingItems.value);
        };
        const debugMode = computed(() => portalStore.state.value.debugMode);
        const getDebugStatusIndicator = (workflowItem) => {
            if (!workflowItem)
                return;
            if (workflowItem.options.disabled)
                return '🏳️';
            if (hasStatus(workflowItem, WorkflowItemStatus.COMPLETE))
                return '✔️';
            if (hasStatus(workflowItem, WorkflowItemStatus.PENDING))
                return '▢';
            return '⨯';
        };
        const getIsWorkflowItemFailed = (workflowItem) => hasStatus(workflowItem, WorkflowItemStatus.FAILED);
        const getFormattedWorkflowItemID = (workflowItem) => formatCamelCase(workflowItem.id.toString());
        const isNextActionable = (workflowItem) => Object.keys(diffObjectsDeep(workflowItem, nextActionableItem.value)).length === 0;
        const getChildWorkflowContext = (workflowItem) => {
            if (!workflowItem.recordMeta.childWorkflow) {
                return {
                    label: '',
                    extraSeed: '',
                };
            }
            const { childWorkflow } = workflowItem.recordMeta;
            return {
                label: formatCamelCase(childWorkflow.context.toString()),
                extraSeed: `${workflowItem.recordMeta.referenceID}-${childWorkflow.key}`,
            };
        };
        const getDisplayLine = (workflowItem) => {
            let text = getFormattedWorkflowItemID(workflowItem);
            if (isNextActionable(workflowItem) && isUnlocked.value) {
                text += ' ⇦';
            }
            /** Remove all numbers */
            return text.replace(/\d+/g, '');
        };
        const getContextDisplayInfo = (workflowItem) => {
            var _a, _b;
            let context = (_b = (_a = workflowItem === null || workflowItem === void 0 ? void 0 : workflowItem.recordMeta.parent) === null || _a === void 0 ? void 0 : _a.context) !== null && _b !== void 0 ? _b : props.context;
            if (!context) {
                return {
                    label: '',
                    tags: [],
                };
            }
            const tags = [];
            const contextIsFailure = context.toString().includes('failure');
            if (contextIsFailure) {
                context = context.toString().replaceAll('--failure', '');
                tags.push({
                    label: 'Failure',
                    style: TagStyle['3_GREY'],
                });
            }
            if (workflowItem === null || workflowItem === void 0 ? void 0 : workflowItem.options.nonBlocking) {
                tags.push({
                    label: 'Non-blocking',
                    style: TagStyle['3_ORANGE'],
                });
            }
            return {
                label: formatCamelCase(context.toString()),
                tags,
            };
        };
        const contextDisplayInfo = computed(() => getContextDisplayInfo());
        return {
            WorkflowItemStatus,
            debugMode,
            allowNextActionableItem,
            allowAction,
            contextDisplayInfo,
            getContextDisplayInfo,
            getIsWorkflowItemFailed,
            formatCamelCase,
            getUniqueWorkflowItemID,
            getDisplayLine,
            getChildWorkflowContext,
            getDebugStatusIndicator,
            hasStatus,
            isChildWorkflowComplete,
            isNextActionable,
            isUnlocked,
            openModal,
            inlineKey,
            logItem,
            componentDef,
            nextActionableItem,
            handleComplete,
            shouldDisplayButton,
            buttonProps,
            viewAs,
        };
    },
});

export { script as default };
